/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useGraph } from 'components/layouts/components/visualisation/wrapper';
import { initGraph } from 'components/layouts/components/visualisation/container/initial/graph';
import { formattedData } from '../components/layouts/components/visualisation/helpers/format-node';
import { initData } from '../components/layouts/components/visualisation/container/initial/nodes';
import { Edges, Nodes } from '../api/visualisation/use-get-data';
import { useProject } from '../context/project-context';
import { useLocation } from 'react-router-dom';
import { expandByNodeData, graphRender } from '../components/layouts/components/visualisation/helpers/utils';
import { nodeLabelCfgStyle } from '../components/layouts/components/visualisation/helpers/constants';
import { findWorkPlaceWidthCentre } from 'helpers/utils';

type GraphRef = React.MutableRefObject<HTMLDivElement | null>;

type Props = ({
  nodes,
  edges,
  count,
  relationsCounts,
  defaultImages,
}: {
  nodes: Nodes | undefined;
  edges: Edges | undefined;
  count: number;
  relationsCounts: { [key: string]: number };
  defaultImages: { [key: string]: string };
}) => GraphRef;

export const useGraphRef: Props = ({ nodes, edges, count, relationsCounts, defaultImages }) => {
  const { state } = useLocation();
  const { projectInfo } = useProject();

  const { graph, setGraph, graphInfo, ...params } = useGraph() ?? {};

  const ref: GraphRef = React.useRef(null);

  useEffect(() => {
    if (graph === undefined && setGraph !== undefined && Object.hasOwn(projectInfo ?? {}, 'role')) {
      setGraph(initGraph(ref.current as HTMLDivElement, params, projectInfo));
    }

    if (graph !== undefined && nodes !== undefined && edges !== undefined) {
      if (state?.data) {
        focus();
      } else {
        const data = formattedData(nodes, edges, relationsCounts, defaultImages);

        if (data !== undefined) initData(graph, data);
      }

      graphRender(graph);
    }
    params.setGraphInfo && params.setGraphInfo({ nodeCount: nodes?.length, nodeCountAPI: count });
  }, [nodes, edges, graph, setGraph, projectInfo, count]);

  useEffect(() => {
    if (graph) {
      const timeoutId = setTimeout(() => {
        graph.updateLayout(
          {
            ...params,
            center: [findWorkPlaceWidthCentre(window.innerWidth, graphInfo?.collapsedWidth), window.innerHeight],
          },
          'center',
          { x: findWorkPlaceWidthCentre(window.innerWidth, graphInfo?.collapsedWidth), y: window.innerHeight / 2 },
          true
        );
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [graphInfo?.collapsedWidth]);

  const focus = () => {
    const { nodes } = formattedData(state?.data.nodes, [], state?.data.relationsCounts, state?.defaultImages);
    if (nodes !== undefined) initData(graph, { nodes, edges: [] });

    const focuses = async () => {
      const node = nodes[0];

      graph.addItem('node', {
        ...node,
        labelCfg: nodeLabelCfgStyle,
      });

      const item = graph.getNodes()[0];

      await expandByNodeData(graph, item, node?.id, '', 'all', params.setGraphInfo);

      setTimeout(() => {
        graph.fitView();
        graph.fitCenter(true);
      }, 100);
    };

    focuses().then();
  };

  return ref;
};
